import React, { useEffect, useState } from "react";
import { Fingerprint, Lock, ShieldCheck, Eye, EyeOff } from "lucide-react";
import { BottomSheet } from "@/components/BottomSheet";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import {
  biometryLabel,
  checkBiometricAvailability,
  clearBiometricCredentials,
  isBiometricEnabled,
  storeCredentials,
  verifyBiometric,
} from "@/lib/biometric";

/**
 * Sign-in & security sheet.
 *
 * - Toggle to enable Face ID / Touch ID / Fingerprint sign-in.
 *   • To enable: user must re-enter their password (we verify against
 *     /api/auth/login) AND pass a biometric prompt. Only after both
 *     succeed do we persist email+password locally so the next sign-in
 *     can auto-fill on biometric verify.
 *   • To disable: stored creds are cleared immediately.
 * - On platforms without a real native authenticator (web preview),
 *   the toggle is disabled with an explanation.
 */
export function SecuritySettingsSheet({ open, onClose }) {
  const { user } = useAuth();
  const [avail, setAvail] = useState({ available: false, biometryType: "none" });
  const [enabled, setEnabled] = useState(isBiometricEnabled());
  const [showEnableForm, setShowEnableForm] = useState(false);
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setEnabled(isBiometricEnabled());
    setShowEnableForm(false);
    setPassword("");
    setError("");
    checkBiometricAvailability().then(setAvail);
  }, [open]);

  const label = biometryLabel(avail.biometryType);

  // Friendly copy for each failure-reason the plugin can return. Keys
  // mirror what `verifyBiometric` returns.
  const REASON_COPY = {
    userCancel:      `${label} cancelled. Try again whenever you're ready.`,
    appCancel:       `Something interrupted ${label}. Try again.`,
    systemCancel:    `iOS interrupted ${label} (maybe a call came in). Try again.`,
    authenticationFailed: `${label} didn't recognise you. Try again — or use your password.`,
    notEnrolled:     `Set up ${label} in your phone's Settings first, then come back.`,
    biometryNotAvailable: `${label} isn't available on this device.`,
    passcodeNotSet:  "You need a device passcode/PIN set in iOS Settings before Kinly can use Face ID.",
    plugin_not_synced: "This build doesn't include native biometric support yet. The next TestFlight update will fix it.",
    timeout:           `${label} took too long to respond. Try again or use your password.`,
    web:             `${label} only works in the iOS or Android app.`,
  };

  const onToggle = () => {
    if (enabled) {
      // Optimistic disable — clear local creds immediately.
      clearBiometricCredentials();
      setEnabled(false);
      return;
    }
    setShowEnableForm(true);
  };

  const onEnable = async (e) => {
    e?.preventDefault?.();
    if (!password) { setError("Enter your password to confirm."); return; }
    setSubmitting(true); setError("");
    try {
      // 1) Verify the password against the server.
      await api.post("/auth/login", { email: user.email, password });
      // 2) Verify biometric — must succeed to prove physical possession.
      const result = await verifyBiometric({
        reason: `Enable ${label} sign-in for Kinly`,
      });
      if (!result.ok) {
        const copy = REASON_COPY[result.reason] || result.message ||
          `${label} verification was cancelled or failed.`;
        setError(copy);
        return;
      }
      // 3) Persist credentials.
      storeCredentials({ email: user.email, password });
      setEnabled(true);
      setShowEnableForm(false);
      setPassword("");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not verify password.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <BottomSheet open={open} onClose={onClose} title="Sign-in & security" fullHeight>
      <div className="space-y-4 pb-4" data-testid="security-settings-sheet">

        <article className="kinly-card" data-testid="security-bio-card">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-primary/15 text-primary inline-flex items-center justify-center shrink-0">
              <Fingerprint className="w-4 h-4" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between gap-3">
                <p className="font-display text-[15px] font-medium">{label} sign-in</p>
                <Toggle
                  testid="security-bio-toggle"
                  checked={enabled}
                  onChange={onToggle}
                  // We DELIBERATELY do not disable the toggle when the
                  // plugin's checkBiometry() reports unavailable — Apple's
                  // chicken-and-egg API requires at least one auth attempt
                  // before isAvailable flips to true. Only the web preview
                  // (no native plugin host) actually disables the toggle.
                  disabled={avail.reason === "web"}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                {avail.reason === "web"
                  ? "Biometric unlock requires the iOS or Android app. Open Kinly on your phone to use this."
                  : `Skip your password. Unlock Kinly with ${label} when you sign in.`}
              </p>
            </div>
          </div>

          {showEnableForm && avail.reason !== "web" && (
            <form onSubmit={onEnable} className="mt-4 pt-4 border-t border-border/60 space-y-3">
              <p className="text-[12px] text-muted-foreground leading-relaxed">
                Confirm your password — we&apos;ll then ask {label} to enable it.
              </p>
              <label className="block">
                <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-medium">Your password</span>
                <div className="mt-1 flex items-center gap-2 border-b-2 border-border focus-within:border-primary py-2">
                  <Lock className="w-4 h-4 text-muted-foreground" strokeWidth={1.7} />
                  <input
                    data-testid="security-bio-password"
                    type={showPw ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    placeholder="Your account password"
                    className="flex-1 bg-transparent outline-none text-[17px]"
                    autoFocus
                  />
                  <button
                    data-testid="security-bio-password-toggle"
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    aria-label={showPw ? "Hide password" : "Show password"}
                    className="tap p-1 text-muted-foreground hover:text-foreground"
                  >
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </label>
              {error && (
                <p data-testid="security-bio-error" className="text-sm text-destructive">{error}</p>
              )}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => { setShowEnableForm(false); setPassword(""); setError(""); }}
                  className="flex-1 h-11 rounded-xl border border-border text-foreground text-sm font-medium tap"
                  data-testid="security-bio-cancel"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || !password}
                  data-testid="security-bio-confirm"
                  className="flex-1 h-11 rounded-xl bg-foreground text-background text-sm font-medium tap inline-flex items-center justify-center gap-2 disabled:opacity-40"
                >
                  <ShieldCheck className="w-4 h-4" />
                  {submitting ? "Verifying…" : `Enable ${label}`}
                </button>
              </div>
            </form>
          )}
        </article>

        <p className="text-[11px] text-muted-foreground leading-relaxed px-1">
          Your password stays on this device. Signing out or removing the app clears the saved unlock.
        </p>
      </div>
    </BottomSheet>
  );
}

function Toggle({ checked, onChange, disabled, testid }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      data-testid={testid}
      onClick={onChange}
      disabled={disabled}
      className={[
        "relative h-7 w-12 rounded-full transition-colors tap shrink-0",
        checked ? "bg-foreground" : "bg-muted",
        disabled ? "opacity-40" : "",
      ].join(" ")}
    >
      <span
        className={[
          "absolute top-0.5 left-0.5 h-6 w-6 rounded-full bg-background shadow transition-transform",
          checked ? "translate-x-5" : "translate-x-0",
        ].join(" ")}
      />
    </button>
  );
}
